/**
 * UI固定文言の繁体字中国語(台湾)辞書。キーは日本語の原文。
 *
 * - 値の {name} プレースホルダは原文と同じ名前で残すこと(t() が置換する)
 * - 辞書に無い文は日本語のまま表示される(欠落が見えるように)
 * - 網羅の検査は web/src/lib/i18n.test.ts(t("...") リテラルとラベル定数を走査)
 * - 訳語は欣葉(台湾)の現場向け。用語の最終確認は現地スタッフに委ねる
 */
export const ZH: Record<string, string> = {
  // ── 共通・ナビ ─────────────────────────────
  "イチニンマエ": "一人前",
  "品目": "菜品",
  "作業の評価": "作業評核",
  "習熟度": "熟練度",
  "ログアウト": "登出",
  "読み込み中…": "載入中…",
  "読み込みに失敗しました:": "載入失敗:",
  "処理中…": "處理中…",
  "保存中…": "儲存中…",
  "完了": "完成",
  "状態": "狀態",
  "更新": "更新",
  "版": "版本",
  "件": "件",
  "回": "次",
  "点": "分",
  "味": "風味",
  "工程": "步驟",
  "第{n}版": "第{n}版",
  "{n}点": "{n}分",
  "{n}本目": "第{n}個檔案",
  "1本のみ": "僅1個檔案",
  "{n}回目": "第{n}次",
  "{m}分{s}秒": "{m}分{s}秒",
  "戻る": "返回",
  "やめる": "取消",
  "閉じる": "關閉",
  "外す": "移除",
  "追加": "新增",
  "すべて": "全部",
  "ありません。": "無。",
  "別タブで開く": "在新分頁開啟",
  "別のタブで開く": "在新分頁開啟",
  "もう一度読み込む": "重新載入",
  "エラー": "錯誤",
  "テナントが未確定です。ログインし直してください": "尚未確認所屬租戶。請重新登入",

  // ── ログイン・登録待ち ─────────────────────
  "社内アカウントでログインしてください。": "請使用公司帳號登入。",
  "Google でログイン": "使用 Google 登入",
  "Google アカウントが無い方はこちら": "沒有 Google 帳號請按這裡",
  "Google サインインがまだ有効化されていません。Firebase コンソールで有効にしてください。":
    "Google 登入尚未啟用。請在 Firebase 主控台啟用。",
  "AI店長でログイン": "使用 AI店長 登入",
  "ポップアップが止められました。このサイトのポップアップを許可してから、もう一度お試しください。":
    "彈出視窗遭到封鎖。請允許此網站的彈出視窗後再試一次。",
  "AI店長の画面が閉じられました。もう一度お試しください。": "AI店長 的畫面已關閉。請再試一次。",
  "時間切れになりました。もう一度お試しください。": "已逾時。請再試一次。",
  "招待が必要です。AI店長のスタッフ登録が、イチニンマエの利用者とまだ結ばれていません。管理者に連絡してください。":
    "需要邀請。AI店長 的員工登錄尚未與一人前的使用者連結。請聯絡管理員。",
  "AI店長のこの役割では、イチニンマエの画面に入れません。AI店長のホームから閲覧してください。":
    "以 AI店長 的此角色無法進入一人前的畫面。請從 AI店長 的首頁瀏覽。",
  "AI店長のログインを引き換えられませんでした。時間をおいて、もう一度お試しください。":
    "無法以 AI店長 的登入換取登入。請稍後再試一次。",
  "メールアドレス": "電子郵件",
  "ログインリンクを送る": "傳送登入連結",
  "送信中…": "傳送中…",
  "{email} にログイン用のリンクを送りました。": "已將登入連結寄至 {email}。",
  "数分待っても届かない場合は迷惑メールをご確認ください。それでも届かないときは Google でログインしてください。":
    "若等候數分鐘仍未收到，請確認垃圾郵件。仍未收到時請改用 Google 登入。",
  "※ 現在この方式はメールが届かない不具合が確認されています。可能な限り Google でのログインをお使いください。":
    "※ 目前此方式已知有郵件無法送達的問題。請儘量使用 Google 登入。",
  "ログインの有効期限が切れている可能性があります。ログインし直してください (401)":
    "登入可能已過期。請重新登入 (401)",
  "登録待ち": "等待註冊",
  "{email} でログインしましたが、このアカウントはまだ利用者として登録されていません。":
    "已以 {email} 登入，但此帳號尚未註冊為使用者。",
  "管理者が役割と担当品目を設定すると利用できるようになります。":
    "待管理者設定角色與負責菜品後即可使用。",

  // ── 役割・状態ラベル(lib/types.ts) ─────────
  "管理者": "管理者",
  "料理長": "主廚",
  "上長": "主管",
  "作業者": "作業人員",
  "未生成": "尚未產生",
  "レビュー中": "審閱中",
  "承認済み": "已核准",
  "マニュアルを生成": "產生手冊",
  "組版": "排版",
  "比較": "比較",
  "回答を本文に反映": "將回覆套用至內文",
  "承認": "核准",
  "中国語版を生成": "產生中文版",
  "待機中": "等待中",
  "実行中": "執行中",
  "失敗": "失敗",
  "素材を差し替え": "更換素材",
  "利用者の割り当て": "指派使用者",
  "食味評価を記録": "記錄風味評核",

  // ── 品目一覧・新規登録 ─────────────────────
  "+ 新しい品目を追加": "+ 新增菜品",
  "まだ品目がありません。「新しい品目を追加」から動画とレシピメモをアップロードしてください。":
    "尚無菜品。請從「新增菜品」上傳影片與食譜筆記。",
  "担当に割り当てられた品目がありません。管理者にご確認ください。":
    "沒有指派給您的菜品。請洽詢管理者。",
  "新しい品目": "新增菜品",
  "料理名 *": "菜名 *",
  "料理名の読み": "菜名讀音",
  "補足メモ(仕込み済みの前提など)": "補充筆記(如已完成的備料等)",
  "例: レバーはカット・水洗い済み。大根漬けと自家製レバーソースは別途仕込み済み。":
    "例: 豬肝已切好並洗淨。醃蘿蔔與自製肝醬已另行備妥。",
  "料理名の意味と、調理開始時点で仕込み済みの食材をここに書いてください。映像に写っていない前提は、書かないと欠落したまま生成されます。":
    "請在此填寫菜名的意義，以及開始烹調時已備妥的食材。影片中看不到的前提若不填寫，產生的手冊會缺漏。",
  "調理動画 *": "烹調影片 *",
  "調理動画": "烹調影片",
  "カメラが自動で分割したファイルはまとめて選んでください。1本の録画として結合して解析します（DJI Osmo Action 5 Pro は約10〜13分ごとに分割します）。":
    "相機自動分段的檔案請一次全部選取。系統會合併成一段錄影後解析（DJI Osmo Action 5 Pro 約每10〜13分鐘分段）。",
  "{n} パートを結合して1本として解析します（合計 {size}）":
    "將合併 {n} 個片段為一段進行解析（共 {size}）",
  "合計 {size}": "共 {size}",
  "レシピ・メモ(PDF / 画像・複数可)": "食譜・筆記(PDF / 圖片・可多個)",
  "レシピ・メモ": "食譜・筆記",
  "アップロードして登録": "上傳並登錄",
  "アップロード中…": "上傳中…",
  "アップロード状況": "上傳進度",
  "アップロード中": "上傳中",

  // ── 品目詳細 ───────────────────────────────
  "← 品目一覧": "← 菜品一覧",
  "← 品目": "← 菜品",
  "アップロード済みの素材": "已上傳的素材",
  "素材を差し替える": "更換素材",
  "名前を押すと中身を確認できます。": "點選名稱即可檢視內容。",
  "マニュアル": "手冊",
  "素材を差し替えた後、まだ生成し直していません。表示されているマニュアルは差し替え前の素材から作られたものです。":
    "更換素材後尚未重新產生。目前顯示的手冊仍是以更換前的素材製作的。",
  "マニュアルを開く": "開啟手冊",
  "レビュー・回答": "審閱・回覆",
  "画面右上の言語切り替え（日本語｜中文）で、マニュアルも日本語版と中国語版が切り替わります。": "以畫面右上的語言切換（日本語｜中文），手冊也會在日文版與中文版之間切換。",
  "確認事項への回答は「レビュー・回答」から、優先度で絞り込んで進められます。":
    "確認事項可從「審閱・回覆」依優先順序篩選作答。",
  "まだ生成されていません。": "尚未產生。",
  "作り直す": "重新產生",
  "生成中…": "產生中…",
  "起動中…": "啟動中…",
  "3回生成して合議し、食い違いを検出します（数分〜十数分かかります）。":
    "會產生3次並交叉比對以偵測出入（需數分鐘〜十多分鐘）。",
  " 作り直すと、これまでの聞き取り事項や回答は引き継がれません（中国語版の翻訳は引き継がれます）。":
    " 重新產生後，先前的訪談事項與回覆不會保留（中文版翻譯會保留）。",
  "中国語版を更新": "更新中文版",
  "本文を繁体字中国語に翻訳し、中国語版マニュアルを組版します。翻訳済みの文は再利用し、変わった箇所だけ翻訳します。":
    "將內文翻譯為繁體中文並排版中文版手冊。已翻譯的句子會重複使用，只翻譯有變動的部分。",
  " 回答の反映などで本文が変わった箇所は日本語のまま表示されるため、そのときはここから更新してください。":
    " 因套用回覆等使內文變動的部分會以日文顯示，屆時請從這裡更新。",
  "回答の反映と承認": "套用回覆與核准",
  "承認する": "核准",
  "承認済み(第{n}版)": "已核准(第{n}版)",
  "承認すると版数が上がり、以後この内容がゴールデン(正)になります。よろしいですか?":
    "核准後版本數會提升，此內容將成為標準(正本)。確定嗎?",
  "反映は「レビュー・回答」で保存した回答を本文へ落とし、SOPを組版し直します。":
    "套用會將「審閱・回覆」中儲存的回覆寫入內文，並重新排版SOP。",
  "承認は未解消の項目が無いときだけ版数が上がります。判断は料理長、反映は運用者、機械は判断しません。":
    "只有在沒有未解決項目時，核准才會提升版本。判斷由主廚負責，套用由營運人員執行，機器不做判斷。",

  // ── 素材の差し替え ─────────────────────────
  "素材の差し替え": "更換素材",
  "素材の差し替えは管理者と料理長のみ行えます。": "只有管理者與主廚可以更換素材。",
  "外した素材は消えません（履歴から中身を確認できます）。誰が何を差し替えたかは実行履歴に残ります。":
    "移除的素材不會被刪除（可從歷史紀錄檢視內容）。誰更換了什麼都會留在執行歷史中。",
  "並び順がそのまま結合順になります。カメラが自動で分割したファイルは、撮影した順に並べてください。":
    "排列順序即為合併順序。相機自動分段的檔案請依拍攝順序排列。",
  "調理動画がありません。1本以上必要です。": "沒有烹調影片。至少需要1個。",
  "調理動画は1本以上必要です。": "烹調影片至少需要1個。",
  "動画を追加": "新增影片",
  "資料を追加(PDF / 画像・複数可)": "新增資料(PDF / 圖片・可多個)",
  "変更の記録": "變更紀錄",
  "変更の理由(任意)": "變更原因(選填)",
  "例: 手元が写っていない区間があったため、撮り直した動画に差し替え":
    "例: 因部分片段拍不到手部動作，更換為重拍的影片",
  "日時・操作者・変更内容は自動で残ります。理由を書いておくと、後から見た人が「なぜ変わったか」を辿れます。":
    "日期時間・操作者・變更內容會自動保存。填寫原因可讓之後的人了解「為何變更」。",
  "この内容で差し替える": "以此內容更換",
  "素材を差し替えても、出ているマニュアルは自動では作り直されません。差し替えた後に「作り直す」を実行してください。よろしいですか?":
    "更換素材後，現有手冊不會自動重新產生。請在更換後執行「重新產生」。確定嗎?",
  "差し替えても、出ているマニュアルは自動では作り直されません。差し替えた後に品目の画面から「作り直す」を実行してください。":
    "更換後，現有手冊不會自動重新產生。請在更換後從菜品頁面執行「重新產生」。",

  // ── 素材プレビュー ─────────────────────────
  "この素材の場所を解決できませんでした（保存先: {path}）。":
    "無法解析此素材的位置（儲存路徑: {path}）。",
  "この素材を表示できませんでした。": "無法顯示此素材。",
  "素材を開けるのは管理者と料理長のみです。権限がある場合は、ログインし直すと解消することがあります。":
    "只有管理者與主廚可以開啟素材。若您有權限，重新登入可能可解決問題。",
  "この形式は画面で表示できません。「別タブで開く」から内容を確認してください。":
    "此格式無法在畫面中顯示。請從「在新分頁開啟」確認內容。",
  "保存先:": "儲存路徑:",
  "アップロード:": "上傳:",

  // ── 実行履歴 ───────────────────────────────
  "実行履歴": "執行歷史",
  "いつ・誰が・何をしたかの記録です。行を押すと変更の中身を確認できます。":
    "記錄何時・誰・做了什麼。點選列可檢視變更內容。",
  "日時": "日期時間",
  "操作内容": "操作內容",
  "操作者": "操作者",
  "操作": "操作",
  "理由:": "原因:",
  "この操作には変更の記録がありません。": "此操作沒有變更紀錄。",
  "変更前後の全体": "變更前後的全部內容",
  "変更前の動画": "變更前的影片",
  "変更後の動画": "變更後的影片",
  "変更前の資料": "變更前的資料",
  "変更後の資料": "變更後的資料",
  "追加した動画": "新增的影片",
  "外した動画": "移除的影片",
  "動画の順序": "影片順序",
  "結合する順序を並べ替えました。": "已重新排列合併順序。",
  "追加した資料": "新增的資料",
  "外した資料": "移除的資料",
  "開始": "開始",
  "終了": "結束",
  "生成回数": "產生次數",
  "{n} 回で合議": "產生{n}次交叉比對",
  "反映した回答": "已套用的回覆",

  // ── マニュアル閲覧(SopViewer) ───────────────
  "日本語版": "日文版",
  "中国語版": "中文版",
  "この品目の中国語版はまだありません。日本語版を表示しています。": "此菜品尚無中文版，目前顯示日文版。",
  "この文書を表示できませんでした。": "無法顯示此文件。",
  "まだ生成されていないか、閲覧権限がない可能性があります。権限の問題であればログインし直すと解消することがあります。":
    "可能尚未產生，或您沒有檢視權限。若是權限問題，重新登入可能可解決。",

  // ── レビュー・回答 ─────────────────────────
  "レビュー": "審閱",
  "マニュアルの確認: {name}": "手冊確認: {name}",
  "{a} / {b} 問 回答済み": "已回覆 {a} / {b} 題",
  "（保留 {n}）": "（保留 {n}）",
  "工程ごとに、マニュアルの本文とその工程の確認事項を並べています。本文を見ながらその場で答えてください。":
    "依步驟排列手冊內文與該步驟的確認事項。請邊看內文邊作答。",
  "確認事項に答えてください。": "請回答確認事項。",
  "入力した内容はこの画面で保存され、品目の画面の「回答を本文に反映」でマニュアルへ落ちます。分からない項目は空欄のまま保存すると保留として残せます。":
    "輸入的內容會在此畫面儲存，並透過菜品頁面的「將回覆套用至內文」寫入手冊。不清楚的項目可留空儲存作為保留。",
  "この品目に確認事項はありません。回答なしで「回答を本文に反映」へ進めます。":
    "此菜品沒有確認事項。可直接進行「將回覆套用至內文」。",
  // ── 工程の削除(#174)・食材の特定(#175) ──
  "この品目に確認事項はありません。不要な工程があれば下でチェックし、「回答を本文に反映」で外せます。":
    "此菜品沒有確認事項。若有不需要的步驟，請在下方勾選，並以「將回覆套用至內文」移除。",
  "不要な工程(他の料理の映り込みなど)は、各工程の「この工程をマニュアルから削除する」にチェックすると、反映時に本文から外れます。":
    "不需要的步驟（如拍到其他菜的畫面）可勾選各步驟的「從手冊中刪除此步驟」，套用時即從內文移除。",
  "この工程をマニュアルから削除する": "從手冊中刪除此步驟",
  "反映時に削除されます": "套用時將刪除",
  "削除する工程 {n} 件": "待刪除的步驟 {n} 項",
  "品目の画面の「回答を本文に反映」を実行すると本文から外れ、工程番号は振り直されます。":
    "在菜品頁面執行「將回覆套用至內文」後，將從內文移除並重新編號。",
  "削除する工程が {n} 件あります。反映すると本文から外れ、工程番号は振り直されます。":
    "有 {n} 個待刪除的步驟。套用後將從內文移除並重新編號。",
  "少なくとも1工程は残す必要があります": "至少須保留 1 個步驟",
  "削除の記録に失敗しました:": "刪除記錄失敗：",
  "削除依頼の読み込みに失敗しました:": "讀取刪除請求失敗：",
  "食材・調味料の特定": "食材與調味料的辨識",
  // ── 材料表(レビュー画面で1箇所にまとめる) ──
  "材料・調味料・分量": "材料・調味料・份量",
  "未記入 {n}": "未填寫 {n}",
  "分量は1皿あたりを数値と単位で書いてください（例: 225g、2尾、大さじ1）。名前が「調味料A」のような仮名のものは、実際の名前を書いてください。この表は優先度の絞り込みに関係なく常に表示します。":
    "份量請以每盤為單位，填寫數值與單位（例: 225g、2尾、1大匙）。名稱為「調味料A」這類暫定名稱者，請填寫實際名稱。此表不受優先度篩選影響，隨時顯示。",
  "食材・調味料": "食材・調味料",
  "分量（1皿あたり）": "份量（每盤）",
  "使用工程": "使用步驟",
  "工程{n}": "步驟{n}",
  "工程{n}: {name}": "步驟{n}：{name}",
  "工程ごとの結果": "各步驟的結果",
  "マニュアルと同じ工程の順に並べています。✓ できている　! 基準と違う　× 見当たらない　? 確認できず": "依照與手冊相同的步驟順序排列。✓ 有做到　! 與標準不同　× 找不到　? 無法確認",
  "盛り付け・工程に紐づかない項目": "擺盤・不屬於特定步驟的項目",
  "確認できず": "無法確認",
  "人数（何皿分）": "人數（幾盤份）",
  "例: 2人前": "例: 2人份",
  "例: 225g": "例: 225g",
  "実際の名前": "實際名稱",
  "名前未登録": "名稱未登錄",
  "映像での見え方: {s}": "影片中的外觀: {s}",
  "工程の構成・自動検出の警告など、特定の工程に属さない項目です。": "步驟結構、自動偵測警告等不屬於特定步驟的項目。",
  // ── 材料の追加・削除(#194) ──
  "この材料を削除する": "刪除此材料",
  // ── マニュアル本体の編集(#203) ──
  "マニュアルを編集": "編輯手冊",
  "残っている確認事項はありません。": "沒有剩餘的確認事項。",
  "マニュアルの編集: {name}": "編輯手冊: {name}",
  "作っていくうちに決まった変更（材料の差し替え・手順の直し）は「マニュアルを編集」から直せます。":
    "製作過程中確定的變更（更換材料・修改步驟）可從「編輯手冊」修改。",
  "作っていくうちに決まった内容（この材料に替える・この手順にする）を直せます。確認事項への回答とは別で、ここで直した内容が本文の正になります。":
    "可修改製作過程中確定的內容（改用此材料・採用此步驟）。與確認事項的回覆不同，此處修改的內容將成為內文的正本。",
  "直した項目は「編集済み」と出ます。保存したあと、品目の画面の「回答を本文に反映」でマニュアルへ落ちます。作り直しても編集は引き継がれます。":
    "修改過的項目會顯示「已編輯」。儲存後，於菜品頁面的「將回覆套用至內文」寫入手冊。即使重新產生也會沿用編輯內容。",
  "マニュアルの編集は管理者と料理長のみ行えます。": "手冊編輯僅限管理員與主廚。",
  "マニュアルがまだありません。品目の画面で「マニュアルを生成」を実行してください (404)":
    "尚未有手冊。請於菜品頁面執行「產生手冊」(404)",
  "マニュアルの取得に失敗しました ({status})": "取得手冊失敗 ({status})",
  "編集内容の読み込みに失敗しました:": "讀取編輯內容失敗：",
  "取り消しに失敗しました:": "取消失敗：",
  "編集済み": "已編輯",
  "元に戻す": "還原",
  "編集をまとめて保存": "一併儲存編輯",
  "{n}件の編集を保存しました。品目の画面の「回答を本文に反映」でマニュアルに反映されます。":
    "已儲存 {n} 項編輯。將於菜品頁面的「將回覆套用至內文」反映至手冊。",
  "全体": "整體",
  "材料・調味料": "材料・調味料",
  "材料の追加・削除は「レビュー・回答」の材料表で行います。ここでは名前と分量を直します。":
    "材料的新增・刪除請於「審閱・回覆」的材料表進行。此處修改名稱與份量。",
  "名前": "名稱",
  "分量（数値）": "份量（數值）",
  "単位": "單位",
  "備考": "備註",
  "人数の補足": "人數補充",
  "工程名": "步驟名稱",
  "作業内容": "作業內容",
  "油温（℃）": "油溫（℃）",
  "油温の目安（状態）": "油溫參考（狀態）",
  "使用器具（1行に1つ）": "使用器具（每行一項）",
  "重要ポイント（1行に1つ）": "重要要點（每行一項）",
  "ありがちなミス（1行に1つ）": "常見錯誤（每行一項）",
  "例: 2": "例: 2",
  "例: 一皿分": "例: 一盤份",
  "例: 225": "例: 225",
  "例: g": "例: g",
  "例: 8切れ": "例: 8塊",
  "例: 強火": "例: 大火",
  "例: 170": "例: 170",
  "例: 煙が薄く立つ程度": "例: 冒出薄煙的程度",
  "マニュアルの編集が {n} 件あります。反映すると本文が書き換わります。":
    "有 {n} 項手冊編輯。套用後內文將被改寫。",
  // ── 承認が適用されなかったときの表示(2026-09-09) ──
  "承認は適用されませんでした": "未套用核准",
  "未解消の確認事項が {n} 件あります。「レビュー・回答」で答えてから、もう一度「承認する」を押してください。空欄のまま保存した保留も未解消として残ります。":
    "尚有 {n} 項未解決的確認事項。請先於「審閱・回覆」中回答，再次點選「核准」。留空儲存的保留項目也視為未解決。",
  "適用されませんでした（未解消 {n} 件）": "未套用（未解決 {n} 項）",
  "・保留 {n}（承認するには回答が要ります）": "・保留 {n}（核准前需要回答）",
  "追加待ち": "待新增",
  "（{s}〜{e} の工程で使用）": "（用於 {s}〜{e} 的步驟）",
  "取り消す": "取消",
  "材料を追加する": "新增材料",
  "食材・調味料の名前（例: 醤油）": "食材・調味料名稱（例: 醬油）",
  "分量（例: 10ml・任意）": "份量（例: 10ml・選填）",
  "使う工程（任意）": "使用的步驟（選填）",
  "追加する": "新增",
  "追加・削除は「回答を本文に反映」で材料表と工程に反映されます。削除した材料は作り直しても戻りません。":
    "新增・刪除會在「將回覆套用至內文」時反映到材料表與步驟。刪除的材料即使重新產生也不會恢復。",
  "変更待ち {n}": "待變更 {n}",
  "材料の変更依頼の読み込みに失敗しました:": "讀取材料變更請求失敗：",
  "材料の変更依頼の記録に失敗しました:": "記錄材料變更請求失敗：",
  "材料表の追加・削除が {n} 件あります。反映すると材料表と工程の参照が変わります。":
    "有 {n} 件材料表的新增・刪除。套用後材料表與步驟的引用將會改變。",
  " 作り直すと、これまでの回答と反映済みの内容はレシピ・メモと同じ前提として解析に渡り、区間が同じ工程・同じ食材にはそのまま引き継がれます（中国語版の翻訳も引き継がれます）。":
    " 重新產生時，先前的回覆與已套用的內容會與食譜・備忘一同作為解析前提，區間相同的步驟與相同食材將直接沿用（中文版翻譯亦會沿用）。",
  "撮影されていない区間があります": "有未拍攝到的區間",
  "この動画は全体 {total} のうち {ratio}% しか撮影されていません（未記録 {missing}）。カメラが止まっている間に進んだ工程は解析に含まれないため、このまま確定すると、その工程は採点でも抜けます。":
    "此影片全長 {total} 中只拍到 {ratio}%（未錄到 {missing}）。相機停止期間進行的步驟不會納入解析，若就此定稿，該步驟在評分時也會缺漏。",
  "工程が欠けている場合は、確定せずに撮り直しをご検討ください。":
    "若步驟有缺漏，請考慮重新拍攝後再定稿。",
  "優先度": "優先度",
  "高": "高",
  "中": "中",
  "低": "低",
  "放置すると内容が誤ったまま残ります": "若不處理，錯誤內容會被保留",
  "味と再現性に効きます": "影響風味與重現性",
  "数値の記入です": "填寫數值",
  "未回答": "未回覆",
  "該当する質問はありません。": "沒有符合的問題。",
  "工程{n}:": "步驟{n}:",
  "確認事項 {n}": "確認事項 {n}",
  "投入する食材": "投入的食材",
  "使用器具": "使用器具",
  "完了基準": "完成標準",
  "重要ポイント": "重要要點",
  "盛り付け基準": "擺盤標準",
  "全体の確認事項": "整體確認事項",
  "人数・アレルギー表示・工程の構成など、特定の工程に属さない項目です。":
    "份數・過敏原標示・步驟結構等不屬於特定步驟的項目。",
  "回答（空欄のまま保存すると保留になります）": "回覆（留空儲存即為保留）",
  "回答を保存": "儲存回覆",
  "保存済み": "已儲存",
  "保留として登録": "登錄為保留",
  "回答済み": "已回覆",
  "保留": "保留",
  "回答を保存しました": "已儲存回覆",
  "保留として記録しました": "已記錄為保留",
  "{n}件を保存しました": "已儲存 {n} 件",
  "未保存 {n}件": "未儲存 {n} 件",
  "入力した内容をまとめて保存": "一次儲存輸入的內容",
  "保存に失敗しました:": "儲存失敗:",
  "まとめての保存に失敗しました:": "批次儲存失敗:",
  "回答の読み込みに失敗しました:": "載入回覆失敗:",
  "質問がまだ作られていません。品目の画面で「マニュアルを生成」を実行してください (404)":
    "尚未建立問題。請在菜品頁面執行「產生手冊」(404)",
  "質問の取得に失敗しました ({status})": "取得問題失敗 ({status})",
  // レビュー質問カテゴリ(lib/review.ts)
  "分量(材料表)": "份量(材料表)",
  "人数": "份數",
  "油温": "油溫",
  "味見の基準": "試味標準",
  "アレルギー表示の確認": "過敏原標示確認",
  "仕込み(動画外の前工程)": "備料(影片外的前置步驟)",
  "判断が分かれた項目": "判斷分歧的項目",
  "映像から読めなかった項目": "無法從影像判讀的項目",
  "量の具体化": "份量具體化",
  "自動検出の警告": "自動偵測的警告",

  // ── 作業の評価 ─────────────────────────────
  "自分の作業動画を評価する": "評核自己的作業影片",
  "評価できる品目がありません。マニュアルが作成済みで、担当に割り当てられた品目が必要です。":
    "沒有可評核的菜品。需要已建立手冊且指派給您的菜品。",
  "選択してください": "請選擇",
  "（マニュアル未承認）": "（手冊未核准）",
  "この品目のマニュアルはまだ承認されていません。評価結果はレビュー前の内容を基準にしたものになります。":
    "此菜品的手冊尚未核准。評核結果將以審閱前的內容為基準。",
  "作業動画": "作業影片",
  "カメラが自動で分割したファイルはまとめて選んでください。1本の録画として結合して評価します。":
    "相機自動分段的檔案請一次全部選取。系統會合併成一段錄影後評核。",
  "アップロードして評価する": "上傳並評核",
  "本日の上限に達しました": "已達今日上限",
  "マニュアルと比べて差分を評価します（数分〜十数分かかります）。1日3回まで実行できます":
    "與手冊比對差異進行評核（需數分鐘〜十多分鐘）。每天最多可執行3次",
  "（本日の回数制限なし）": "（今日無次數限制）",
  "（本日の残り {n} 回）": "（今日剩餘 {n} 次）",
  "評価を開始しました。": "已開始評核。",
  "評価を開始しました。本日はあと {n} 回実行できます。": "已開始評核。今日還可執行 {n} 次。",
  "評価結果": "評核結果",
  "まだ評価がありません。": "尚無評核。",
  "スコア": "分數",
  "実行": "執行",
  "基準マニュアルは未承認": "基準手冊未核准",
  "評価中": "評核中",

  // ── 評価レポート ───────────────────────────
  "← 評価一覧": "← 評核一覧",
  "評価結果を取得できませんでした ({status})": "無法取得評核結果 ({status})",
  "通信簿": "成績單",
  "試食待ち": "待試吃",
  "味は担当のコーチが試食して評価します": "風味由負責的教練試吃後評核",
  "総合スコア": "總分",
  "直したいところ": "需要改進之處",
  "できていたところ": "做得好之處",
  "確認できなかった": "無法確認",
  "減点していません": "未扣分",
  "評価した動画": "評核的影片",
  "この評価に使った動画です（画質は 720p に落としてあります。分割して撮影されたファイルは1本に結合しています）。":
    "此評核使用的影片（畫質已降為 720p。分段拍攝的檔案已合併為一段）。",
  "この評価には動画が残っていません（動画を保存するようにする前に実行された評価です）。もう一度評価を実行すると、次からはここで見られます。":
    "此評核沒有保留影片（是在開始保存影片之前執行的評核）。再次執行評核後即可在此觀看。",
  "項目ごとの充足": "各項目達成度",
  "マニュアルの項目が作業動画で確認できるかを複数回みて、多数決で判定しています。工程の分け方や順序、作業の速さは評価に入れていません。":
    "以多次觀察並多數決判定手冊項目能否在作業影片中確認。步驟的劃分・順序・作業速度不列入評核。",
  "直したいところ（{n}件）": "需要改進之處（{n}件）",
  "マニュアルの項目はすべて確認できました。": "手冊的所有項目皆已確認。",
  "動画では:": "影片中:",
  "確認できなかったところ（{n}件）": "無法確認之處（{n}件）",
  "動画に映っていないか、判別できなかった項目です。評価は下げていません。手元が映るように撮り直すと、次はここも確認できます。":
    "影片中未拍到或無法判別的項目。未因此扣分。重拍時讓手部動作入鏡，下次即可確認這些項目。",
  "基準と違うと判定されましたが、何が違うかが示されなかったため保留しました。":
    "雖判定與基準不同，但未指出差異之處，故列為保留。",
  "マニュアルに無かった動き（{n}件）": "手冊中沒有的動作（{n}件）",
  "余分とは限りません。お手本に足すべき手順であれば、マニュアル側を直してください。":
    "不一定是多餘的。若是應加入範本的步驟，請修改手冊。",
  "できていたところ（{n}件）": "做得好之處（{n}件）",
  "全体の講評": "整體講評",
  "元のレポート（テキスト版）を開く": "開啟原始報告（文字版）",
  // 評価カテゴリ・判定・帯(lib/evaluation.ts)
  "食材": "食材",
  "火加減": "火候",
  "盛り付け": "擺盤",
  "できている": "有做到",
  "基準と違う": "與基準不同",
  "見当たらない": "未看到",
  "良好": "良好",
  "継続指導": "持續指導",
  "要重点指導": "需重點指導",

  // ── 食味評価 ───────────────────────────────
  "食味評価": "風味評核",
  "まだ食味の評価がありません。試食のうえ評価が入ると、ここに表示されます。":
    "尚無風味評核。試吃並輸入評核後會顯示於此。",
  "食味を評価する": "評核風味",
  "自分の評価を修正する": "修改自己的評核",
  "この軸へのコメント（任意）": "此項目的評語（選填）",
  "全体への一言（任意）": "整體評語（選填）",
  "評価を保存する": "儲存評核",
  "評価を更新する": "更新評核",
  "食味の評価を保存しました。": "已儲存風味評核。",
  "すべての軸に1〜5の評価を入れると保存できます。": "所有項目皆輸入1〜5的評分後即可儲存。",

  // ── 習熟度ダッシュボード ───────────────────
  "習熟度ダッシュボード": "熟練度儀表板",
  "この画面は料理長・管理者のみが利用できます。ご自身の採点結果は":
    "此畫面僅限主廚・管理者使用。您自己的評分結果可從",
  "「作業の評価」": "「作業評核」",
  "から確認できます。": "確認。",
  "スタッフ × 品目｜お手本との比較採点": "員工 × 菜品｜與範本比對評分",
  "平均 習熟度": "平均熟練度",
  "一人前 到達": "達到一人前",
  "スタッフ×品目": "員工×菜品",
  "今月の採点回数": "本月評分次數",
  "習熟度マトリクス": "熟練度矩陣",
  "採点対象がまだありません。マニュアルが作成済みの品目と、採点が1件以上必要です。":
    "尚無評分對象。需要已建立手冊的菜品與至少1筆評分。",
  "スタッフ": "員工",
  "一人前": "一人前",
  "初回採点": "首次評分",
  "80点以上": "80分以上",
  "60–79点": "60–79分",
  "60点未満": "未滿60分",
  "朱印": "朱印",
  "＝ 一人前到達（{n}点以上を2回連続）": "＝ 達到一人前（連續2次{n}分以上）",
  "点数を押すと、その回の評価結果が開きます": "點選分數可開啟該次的評核結果",
  "習熟度の推移": "熟練度變化",
  "この品目の採点はまだありません。": "此菜品尚無評分。",
  "一人前ライン（{n}点）": "一人前門檻（{n}分）",
  "要指導": "需指導",
  "経過観察": "觀察中",
  "最新 {n}点": "最新 {n}分",
  "（前回比 {n}）": "（與上次相比 {n}）",
  "評価結果を見る": "檢視評核結果",
  "80点未満の組み合わせはありません。": "沒有未滿80分的組合。",
  "どの工程を直すべきかは、各評価結果の「直したいところ」に出ています。":
    "應改進哪些步驟，請見各評核結果的「需要改進之處」。",

  // ── マニュアルの完成度(lib/completeness.ts) ──
  "この品目の完成度": "本品項的完成度",
  "そのまま読める": "可直接閱讀",
  "軽い補足で足りる": "略作補充即可",
  "補足が要る": "需要補充",
  "素材追加を検討": "建議補拍素材",
  "撮影率が高く、仕込み済み食材の欠落もほぼありません。確認事項に答えるだけで確定できます。":
    "拍攝涵蓋率高，備料食材的缺漏也極少。只需回答確認事項即可定稿。",
  "未記録は数分以内です。指摘の中心は「仕込み済み食材の作り方が別途必要」です。":
    "未記錄時間在數分鐘以內。主要問題是「備料食材的作法需另行記錄」。",
  "未記録区間が長いか、仕込み済み食材が多く残っています。レシピ・加藤メモでの補足が要ります。":
    "未記錄區間較長，或備料食材殘留較多。需要以食譜與備註補充。",
  "未記録区間が長く、かつ仕込み済み食材が多い状態です。映像の追加なしに完成させるのは難しい可能性があります。":
    "未記錄區間長且備料食材多。若不補充影像，可能難以完成。",
  "{n} 工程": "{n} 道工序",
  "動画の尺": "影片長度",
  "{n} 分": "{n} 分鐘",
  "（うち実作業 {n} 分）": "（其中實際作業 {n} 分鐘）",
  "撮影率": "拍攝涵蓋率",
  "{n}%": "{n}%",
  "（未記録 {n} 分）": "（未記錄 {n} 分鐘）",
  "{n} 種": "{n} 種",
  "仕込みSOP未作成": "備料SOP未建立",
  "なし": "無",
  "{n} 件": "{n} 件",
  "要確認": "待確認",
  "警告": "警告",
  "撮影率は、動画のうち工程または下準備として記録された割合です。低い品目はSOPに「撮影範囲外」が並ぶため、レシピ・メモでの補足が要ります。":
    "拍攝涵蓋率是指影片中被記錄為工序或前置準備的比例。比例低的品項，SOP 上會出現多處「拍攝範圍外」，需以食譜與備註補充。",
  "「仕込みSOP未作成」は、調理開始時点で既に出来ていた食材のうち作り方が記録されていないものです。市販品なら対象から外せます。":
    "「備料SOP未建立」是指開始烹調時已完成、但作法未被記錄的食材。若為市售品可排除。",

  // ── 仕込みSOPの紐づけ(#18) ──
  "紐づける…": "建立關聯…",
  // ── 仕込みの作り方をレシピから取り込む(#250) ──
  "仕込みの紐づけ": "事前製備的關聯",
  "仕込みの作り方をレシピから取り込み": "從食譜匯入事前製備作法",
  "レシピを選ぶか、PDFを選んでください": "請選擇食譜或PDF",
  "レシピから作り方を取り込んでいます（数分かかります）。終わるとこの画面に反映されます。": "正在從食譜匯入作法(需要數分鐘)。完成後會反映在此畫面。",
  "前回の取り込みは失敗しました:": "上次匯入失敗:",
  "別のレシピから取り込み直す": "從其他食譜重新匯入",
  "動画が無い場合: レシピ（PDF）から作り方を取り込む": "沒有影片時: 從食譜(PDF)匯入作法",
  "選んだレシピから「{name}」の材料・分量・手順だけを抜き出し、マニュアルの「仕込み」に載せます。レシピに書いていないことは補いません。": "從所選食譜中僅擷取「{name}」的材料・份量・步驟,放入手冊的「事前製備」。食譜未記載的內容不會補上。",
  "品目のレシピ・メモから選ぶ": "從菜品的食譜・備忘錄選擇",
  "または、PDFを新しく上げる": "或上傳新的PDF",
  "レシピのPDFを選ぶ": "選擇食譜PDF",
  "取り込む": "匯入",
  "仕込み（この料理の前に必要な準備）": "事前製備(製作此道菜前需準備)",
  "作り方が未記録": "作法未記錄",
  "仕込みSOPあり": "已有事前製備SOP",
  "市販品・外部調達": "市售品・外部採購",
  "レシピから取り込み済み": "已從食譜匯入",
  "材料": "材料",
  // ── 仕込みの作り方の修正(#259) ──
  "作り方を修正する": "修改作法",
  "保存": "儲存",
  "「{name}」の作り方を直します。保存した内容は「回答を本文に反映」でマニュアルに入ります。": "修改「{name}」的作法。儲存的內容會在「將回答反映至本文」時放入手冊。",
  "材料と分量": "材料與份量",
  "材料名": "材料名稱",
  "分量（例: 500g）": "份量(例: 500g)",
  "この行を削除": "刪除此列",
  "材料の行を追加": "新增材料列",
  "手順（1行に1つ）": "步驟(每行一個)",
  "手順を1つ以上書いてください": "請至少填寫一個步驟",
  "補足（保存方法・出典など。1行に1つ）": "補充(保存方法・出處等。每行一個)",
  "修正を取り消す": "取消修改",
  "市販品・外部調達（仕込みSOP不要）": "市售品・外部採購（不需備料SOP）",

  // ── 料理名の変更(components/RenameDish.tsx・履歴の台帳行) ──
  "料理名を変更": "變更菜名",
  "料理名を変更する": "變更菜名",
  "空欄にすると読みを消します": "留空即可清除讀音",
  "変更は一覧・履歴にすぐ反映されます。マニュアル本文の見出しは、次にマニュアルを生成または再組版したときに新しい名前になります。":
    "變更會立即反映在列表與紀錄上。手冊內文的標題會在下次產生或重新排版時換成新名稱。",
  "保存する": "儲存",
  "変更前の料理名": "變更前的菜名",
  "変更後の料理名": "變更後的菜名",

  // ── 品目の段階と進捗(lib/progress.ts・components/Progress.tsx) ──
  "進捗": "進度",
  "情報登録中": "資料登錄中",
  "生成中": "產生中",
  "実行に失敗": "執行失敗",
  "レビュー待ち": "待審閱",
  "再生成待ち": "待重新產生",
  "レビュー承認待ち": "待核准",
  "レビュー承認済み": "已核准",
  "動画とレシピ・メモを登録し、「マニュアルを生成」を実行してください。":
    "請登錄影片與食譜・備註，然後執行「產生手冊」。",
  "実行中です。数分〜十数分かかります。終わると自動で次の段階に進みます。":
    "執行中。需要數分鐘至十幾分鐘。完成後會自動進入下一階段。",
  "直近の実行が失敗しました。品目の画面で理由を確認し、もう一度実行してください。":
    "最近一次執行失敗。請在菜品畫面確認原因後再執行一次。",
  "マニュアルができました。「レビュー・回答」から確認事項に答えてください。":
    "手冊已完成。請從「審閱・回覆」回答確認事項。",
  "確認事項への回答が途中です。保留(空欄で保存)のままでは承認できません。":
    "確認事項的回覆尚未完成。若保持保留（留空儲存）狀態將無法核准。",
  "確認事項への回答が途中です。優先度の高いものから答えてください。":
    "確認事項尚未回答完畢。請從優先度高的項目開始作答。",
  "確認事項に答え終わりました。「回答を本文に反映」を実行してください。":
    "確認事項已全部作答。請執行「將回覆套用至內文」。",
  "料理長が「承認する」を実行すると確定します。": "由主廚執行「核准」即可定稿。",
  "承認済みです。この内容が評価の基準になります。":
    "已核准。此內容將作為評核的基準。",
  "マニュアル生成中": "產生手冊中",
  "組版中": "排版中",
  "比較中": "比較中",
  "回答を反映中": "套用回覆中",
  "承認処理中": "核准處理中",
  "中国語版を生成中": "產生中文版中",
  "動画の登録待ち": "等待登錄影片",
  "生成の実行待ち": "等待執行產生",
  "マニュアルの生成に失敗": "產生手冊失敗",
  "組版に失敗": "排版失敗",
  "比較に失敗": "比較失敗",
  "回答の反映に失敗": "套用回覆失敗",
  "承認に失敗": "核准失敗",
  "中国語版の生成に失敗": "產生中文版失敗",
  "仕込みを紐づけ中": "事前製備關聯中",
  "仕込みをレシピから取り込み中": "正在從食譜匯入事前製備",
  "仕込みの紐づけに失敗": "事前製備關聯失敗",
  "仕込みの取り込みに失敗": "事前製備匯入失敗",
  "（再実行が要ります）": "（需要重新執行）",
  "起動待ち": "等待啟動",
  "{time} から実行中": "自 {time} 起執行中",
  "残っている確認事項はありません": "沒有待確認事項",
  "{done} / {total} 問": "{done} / {total} 題",
  "残り {n} 問": "剩餘 {n} 題",
  "（{done} / {total} 問 完了）": "（已完成 {done} / {total} 題）",
  "（うち保留 {n}）": "（其中保留 {n}）",

  // ── 動画パートの警告(lib/parts.ts の固定文) ──
  "撮影開始時刻の異なるファイルが混ざっています。別々の録画を1本にしようとしていないか確認してください。":
    "混入了拍攝開始時間不同的檔案。請確認是否誤將不同錄影合併為一段。",

  // ── 材料の原料(#216) ──
  "原料（この材料を作るのに使うもの）": "原料（製作此材料所用的原料）",
  "原料名": "原料名稱",
  "原料名（例: アオリイカ）": "原料名稱（例: 軟絲）",
  "歩留まり（%）": "出成率（%）",
  "歩留まり%（例: 60）": "出成率%（例: 60）",
  "この原料を外す": "移除此原料",
  "原料の量（任意）": "原料用量（選填）",
  "単位（例: g）": "單位（例: g）",
  "原料の備考": "原料備註",
  "備考（例: 皮・内臓を除く）": "備註（例: 去皮與內臟）",
  "歩留まりは 1〜100 の数値で書いてください。": "出成率請填寫 1〜100 的數值。",
  "原料の量は数値で書いてください。": "原料用量請填寫數值。",
  "必要な原料の目安: {amount}（{qty} ÷ 歩留まり {yield}%）": "所需原料參考: {amount}（{qty} ÷ 出成率 {yield}%）",
  "原料の量: {amount}": "原料用量: {amount}",
  "＋ 原料を追加": "＋ 新增原料",
  "下処理や加熱が要る材料は、原料と歩留まりを登録できます（例: 茹でイカの原料はアオリイカ、歩留まり60%）。原料の量を空けておくと、分量と歩留まりから必要量の目安を出します。":
    "需要前處理或加熱的材料，可登錄原料與出成率（例: 燙魷魚的原料為軟絲，出成率60%）。原料用量留空時，會依份量與出成率算出所需量參考。",
  "原料: {name}": "原料: {name}",
  "歩留まり {n}%": "出成率 {n}%",

  // ── レビュー画面の工程ごとの修正(#219) ──
  "直した内容は保存すると記録され、品目の画面の「回答を本文に反映」でマニュアルに反映されます。作り直しても引き継がれます。":
    "修改內容儲存後會記錄下來，在菜品頁面按「將回覆套用至內文」後反映到手冊。重新產生時也會沿用。",
  "修正を保存": "儲存修改",
  "保存せずに閉じる": "不儲存並關閉",
  "本文の内容を直したいとき（例: 1cmに切る → 5cmに切る）は、各工程の「この工程を修正する」から直せます。":
    "想修改本文內容時（例: 切成1cm → 切成5cm），可從各步驟的「修改此步驟」進行修改。",
  "修正あり（反映待ち）": "有修改（等待套用）",
  "この工程を修正する": "修改此步驟",

  // ── レビュー画面の材料表からの原料登録(#222) ──
  "「{name}」を作るのに使う原料を登録します。原料の量を空けておくと、分量と歩留まりから必要量の目安を出します。反映は品目の画面の「回答を本文に反映」です。":
    "登錄製作「{name}」所用的原料。原料用量留空時，會依份量與出成率算出所需量參考。請在菜品頁面按「將回覆套用至內文」反映。",
  "原料を保存": "儲存原料",
  "原料の変更あり（反映待ち）": "原料有變更（等待套用）",
  "原料を直す": "修改原料",
  "原料を登録": "登錄原料",

  // ── 材料を他の材料の原料へ移す(#226) ──
  "この材料を他の材料の原料にする": "將此材料改為其他材料的原料",
  "どの材料の原料にしますか？": "要改為哪個材料的原料？",
  "選んでください": "請選擇",
  "「{from}」を「{to}」の原料にします。": "將「{from}」改為「{to}」的原料。",
  "保存すると「{from}」は材料表から削除され（反映時）、「{to}」の原料として残ります。歩留まりを入れると必要量の目安が出ます。":
    "儲存後「{from}」會從材料表刪除（套用時），並保留為「{to}」的原料。填入出成率即可顯示所需量參考。",
  "原料にして保存": "改為原料並儲存",

  // ── 材料表から材料の名前・分量を直す(#230) ──
  "この材料を修正する": "修改此材料",
  "元の名前: {name}": "原名稱：{name}",
  "「{name}」の名前・分量を直します。保存すると記録され、品目の画面の「回答を本文に反映」でマニュアルに反映されます。作り直しても引き継がれます。":
    "修改「{name}」的名稱與份量。儲存後會記錄下來，在菜品頁面按「將回覆套用至內文」後反映到手冊。重新產生時也會沿用。",
  "名前を入力してください": "請輸入名稱",
  "同じ名前の材料が既にあります": "已有相同名稱的材料",
  "分量は数値で入力してください（単位は右の欄に）": "份量請輸入數值（單位請填在右側欄位）",
  // 材料の説明(prep_note)も直せるようにする
  "説明（下処理・何から作るか）": "說明（前處理・由什麼製成）",
  "例: 光中杏・砂糖・水から作ったもの": "例: 由光中杏、砂糖、水製成",
  "材料表で「— 」のあとに出る説明です。空にして保存すると説明を消します。":
    "這是材料表中「— 」後面顯示的說明。清空後儲存即可刪除說明。",

  // ── 原料の入力エラー中は保存させない(#238) ──
  "原料の歩留まり・量に数値でない値があります。赤字の欄を直してから保存してください。":
    "原料的出成率或用量有非數值的內容。請修正紅字欄位後再儲存。",

  // ── 仕込みの取り込みが失敗したときの案内と扱いの選択 ──
  "対応の選択待ち": "待選擇處理方式",
  "「{name}」の作り方が資料にありません。扱いを選んでください": "資料中沒有「{name}」的作法。請選擇處理方式",
  "「{name}」の扱いを切り替えています（数分かかります）。終わるとこの画面に反映されます。":
    "正在切換「{name}」的處理方式(需要數分鐘)。完成後會反映在此畫面。",
  "入力した作り方（反映待ち）": "已輸入的作法(待套用)",
  "「回答を本文に反映」でマニュアルに入ります。": "執行「將回覆套用至內文」後會放入手冊。",
  "入力した作り方を直す": "修改已輸入的作法",
  "「{name}」の作り方は、選んだ資料「{doc}」に書かれていませんでした。": "所選資料「{doc}」中沒有記載「{name}」的作法。",
  "「{name}」の作り方は、選んだ資料に書かれていませんでした。": "所選資料中沒有記載「{name}」的作法。",
  "資料を読んだ結果:": "資料讀取結果:",
  "同じ資料で取り込み直しても結果は変わりません。次のどれかを選んでください。":
    "以同一份資料重新匯入,結果也不會改變。請從下列選項中擇一。",
  "「{name}」の取り込みが途中で止まりました（システム側のエラー）。": "「{name}」的匯入中途停止了(系統端錯誤)。",
  "エラーの内容:": "錯誤內容:",
  "もう一度取り込むか、次のどれかを選んでください。": "請重新匯入,或從下列選項中擇一。",
  "作り方を入力する（料理長が書く）": "輸入作法(由主廚填寫)",
  "別の資料（PDF・写真）から取り込む": "從其他資料(PDF・照片)匯入",
  "資料を選んでもう一度取り込む": "選擇資料並重新匯入",
  "市販品・外部調達にする（仕込みSOPは不要）": "設為市售品・外部採購(不需要事前製備SOP)",
  "自家製として、仕込みを撮影してSOPを作る": "作為自製品,拍攝事前製備並建立SOP",
  "「{name}」を市販品・外部調達として記録します。マニュアルは数分で作り直されます。":
    "將「{name}」記錄為市售品・外部採購。手冊會在數分鐘內重新製作。",
  "「{name}」を自家製（仕込みの撮影が必要）として記録します。マニュアルは数分で作り直されます。":
    "將「{name}」記錄為自製品(需要拍攝事前製備)。手冊會在數分鐘內重新製作。",
  "記録する": "記錄",
  "「{name}」の作り方を書いてください。保存した内容は「回答を本文に反映」でマニュアルに入ります。":
    "請填寫「{name}」的作法。儲存的內容會在執行「將回覆套用至內文」後放入手冊。",
  "作り方を記入済み": "已填寫作法",

  // ── 品目の削除(論理削除・管理者のみ) ──
  "品目を削除": "刪除菜品",
  "削除した品目を元に戻す": "還原已刪除的菜品",
  "この品目を削除する": "刪除此菜品",
  "「{name}」を削除します。": "將刪除「{name}」。",
  "品目一覧・評価の選択肢・熟達度の表から外れます。素材・マニュアル・評価の記録は消えず、この画面の「元に戻す」で戻せます。誰が削除したかは実行履歴に残ります。":
    "將從菜品列表・評核選項・熟練度表中移除。素材・手冊・評核紀錄不會消失,可在此畫面按「還原」復原。刪除者會記錄在執行紀錄中。",
  "削除の理由（任意）": "刪除原因(選填)",
  "例: 別の品目として作り直したため": "例: 已作為其他菜品重新建立",
  "削除中…": "刪除中…",
  "削除する": "刪除",
  "この品目は削除されています（{when}・{name}）。品目一覧には表示されません。": "此菜品已刪除({when}・{name})。不會顯示在菜品列表中。",
  "削除の理由:": "刪除原因:",
  "戻しています…": "還原中…",

  // ── 評価の削除(論理削除・管理者のみ) ──
  "評価を削除": "刪除評核",
  "削除した評価を元に戻す": "還原已刪除的評核",
  "削除": "刪除",
  "「{name}」の評価を削除します。": "將刪除「{name}」的評核。",
  "評価一覧と熟達度の表から外れます。評価の記録と動画は消えず、「削除した評価も表示する」から元に戻せます。本日の評価回数は戻りません。誰が削除したかは品目の実行履歴に残ります。":
    "將從評核列表與熟練度表中移除。評核紀錄與影片不會消失,可從「顯示已刪除的評核」還原。今日已使用的評核次數不會退回。刪除者會記錄在菜品的執行紀錄中。",
  "例: 撮り直したため": "例: 已重新拍攝",
  "削除した評価も表示する（{n}件）": "顯示已刪除的評核({n}項)",
  "削除済み（{when}・{name}）": "已刪除({when}・{name})",
  "この評価は削除されています（{when}・{name}）。評価一覧と熟達度の表には表示されません。":
    "此評核已刪除({when}・{name})。不會顯示在評核列表與熟練度表中。",

  // ── レビュー画面の先頭の「残りの確認事項」 ──
  "残りの確認事項 {n} 問": "剩餘確認事項 {n} 題",
  "すべて答えると「回答を本文に反映」から承認へ進めます。": "全部回答後,即可從「將回覆套用至內文」進入核准。",
  "材料表": "材料表",
  "（保留中）": "(保留中)",
  "（数値として読めませんでした）": "(無法讀取為數值)",
  "数値として読めませんでした。「225g」「2人前」のように数と単位で書き直してください。": "無法讀取為數值。請以「225g」「2人份」這樣的數字加單位重新填寫。",
  "ここへ移動": "前往此處",
  "残りの確認事項を見る": "查看剩餘確認事項",

  // ── 採点の重み(品目詳細) ──
  "採点の重み": "評分權重",
  "作業評価の総合点は、カテゴリごとの「できている」割合をこの重みで平均して出します。品目ごとに変えられます。変更は次の評価から使われ、既に付いた点数は変わりません。":
    "作業評核的總分,是以此權重將各類別「已做到」的比例加權平均而得。可依菜品調整。變更自下次評核起生效,已評出的分數不會改變。",
  "既定 {n}%": "預設 {n}%",
  "合計": "合計",
  "重みは 0〜100 の整数で入力してください": "權重請輸入 0〜100 的整數",
  "重みの合計を100にしてください(いまは{n})": "權重合計請設為100(目前為{n})",
  "採点の重みを保存しました。": "已儲存評分權重。",
  "重みを保存する": "儲存權重",
  "既定に戻す": "恢復預設",
  "変更前の重み": "變更前的權重",
  "変更後の重み": "變更後的權重",
  "採点の重みを変更": "變更評分權重",

  // ── マニュアルを編集: 工程で使う材料の使い方 ──
  "「{name}」の使い方（この工程）": "「{name}」的用法(此步驟)",
  "例: 半量を使用": "例: 使用一半",

  // ── 閲覧記録(#13) ──
  "閲覧記録": "瀏覽紀錄",
  "この品目のマニュアルと素材を、いつ誰が開いたかの記録です（新しい順に最大{n}件。同じ人が5分以内に開き直した分は残りません）。":
    "此品項的手冊與素材由誰在何時開啟的紀錄(依新到舊最多{n}筆。同一人在5分鐘內重新開啟的不會留下紀錄)。",
  "閲覧記録を見る": "查看瀏覽紀錄",
  "まだ閲覧の記録はありません。": "尚無瀏覽紀錄。",
  "閲覧者": "瀏覽者",
  "見たもの": "瀏覽內容",
  "素材": "素材",
  "工程の動画": "步驟影片",

  // ── 利用者の管理（管理者のみ） ──
  "利用者": "使用者",
  "この画面は管理者のみ利用できます。": "此畫面僅限管理者使用。",
  "利用者の管理": "使用者管理",
  "招待中": "邀請中",
  "招待したメールアドレスでログインすると、自動で利用者になります。": "以受邀的電子郵件地址登入後,會自動成為使用者。",
  "役割": "角色",
  "招待した人": "邀請人",
  "招待を取り消す": "取消邀請",
  "担当品目": "負責菜品",
  "自分": "本人",
  "全品目": "全部菜品",
  "{n}品目": "{n} 道菜品",
  "管理者と料理長は全品目を見られます。上長と作業者は、担当に入れた品目だけを見られます。":
    "管理者與主廚可查看全部菜品。主管與作業人員只能查看被指派負責的菜品。",
  "利用者を招待": "邀請使用者",
  "招待した人には、メールは届きません。この画面のアドレス（app.ichininmae.jp）を伝え、招待したメールアドレスでログインしてもらってください（Google アカウントか、ログイン画面のメールリンク）。":
    "系統不會寄送邀請郵件給對方。請告知對方本畫面的網址(app.ichininmae.jp),並請對方以受邀的電子郵件地址登入(Google 帳號,或登入畫面的電子郵件連結)。",
  "メールアドレス *": "電子郵件地址 *",
  "表示名": "顯示名稱",
  "空欄ならログイン時の名前を使います": "留空時使用登入帳號的名稱",
  "{email} を招待しました。": "已邀請 {email}。",
  "招待する": "邀請",
  "{name} の担当品目": "{name} 負責的菜品",
  "品目がまだありません。": "尚無菜品。",
  "「{name}」を利用者から削除します。": "將「{name}」從使用者中刪除。",
  "削除すると、この人はすぐにマニュアルを開けなくなり、ログイン中の画面も使えなくなります。担当品目からも外れます。評価や操作の記録は残ります。もう一度使ってもらうときは、招待し直してください。":
    "刪除後,此人將立即無法開啟手冊,登入中的畫面也將無法使用,並會從負責菜品中移除。評核與操作紀錄會保留。若要再次使用,請重新邀請。",
  "招待を取り消し": "取消邀請",
  "招待を受けて参加": "接受邀請並加入",
  "利用者の設定を変更": "變更使用者設定",
  "担当品目を変更": "變更負責菜品",
  "利用者を削除": "刪除使用者",

  // ── 管理者の同席（同席コード） ──
  "同席コード": "陪同代碼",
  "同席が終わりました。続けて見るには、管理者に新しいコードを出してもらってください。":
    "陪同已結束。若要繼續查看,請管理者提供新的代碼。",
  "このコードは期限が切れています。管理者の画面に出ている新しいコードを入力してください。":
    "此代碼已過期。請輸入管理者畫面上顯示的新代碼。",
  "このコードは使用済みです。管理者の画面に出ている新しいコードを入力してください。":
    "此代碼已被使用。請輸入管理者畫面上顯示的新代碼。",
  "コードが違います。管理者の画面に出ている6桁の数字を入力してください。":
    "代碼不正確。請輸入管理者畫面上顯示的6位數字。",
  "{name} さんの同席で閲覧しています": "{name} 陪同查看中",
  "管理者の同席で閲覧しています": "管理者陪同查看中",
  "管理者の同席が必要です": "需要管理者陪同",
  "マニュアルは、管理者が同席しているときに見られます。管理者の画面に表示された6桁の同席コードを入力してください。":
    "手冊需在管理者陪同時才能查看。請輸入管理者畫面上顯示的6位數陪同代碼。",
  "開く": "開啟",
  "作業者がマニュアルを開くときは、管理者の同席が必要です。作業者のそばでこのコードを見せ、作業者の画面に入力してもらってください。コードは60秒で切り替わり、1回使うと無効になります。":
    "作業人員開啟手冊時需要管理者陪同。請在作業人員身旁出示此代碼,並請對方在其畫面上輸入。代碼每60秒更換一次,使用一次後即失效。",
  "あと {n} 秒": "剩餘 {n} 秒",
  "{name} さんが閲覧を始めました。": "{name} 已開始查看。",
  "表示をやめる": "停止顯示",
  "同席コードを表示する": "顯示陪同代碼",
  "同席中の閲覧": "陪同中的查看",
  "いま同席している閲覧はありません。": "目前沒有陪同中的查看。",
  "打ち切る": "中止",
  "打ち切ると、作業者はすぐにマニュアルを新しく読み込めなくなり、画面も30秒ほどで閉じます。続けるにはもう一度コードの入力が必要です。":
    "中止後,作業人員將立即無法載入新的手冊內容,畫面也會在約30秒內關閉。若要繼續,需再次輸入代碼。",
  "管理者の同席で閲覧を開始": "在管理者陪同下開始查看",
  "同席での閲覧を終了": "結束陪同查看",
};
